import { NFCParams } from '../types';

/**
 * Parse NFC ID or Session Hash from URL path
 * Supported formats:
 * - NFC ID: https://chat.tablechat.io/B23456789012345678900
 * - Session Hash: https://chat.tablechat.io/A7kM9pLx3nQ2wE8rT5yU
 * Simple, fast, and scalable for high-volume usage.
 */
export const parseNFCParams = (url?: string): NFCParams | null => {
  try {
    const currentUrl = url || window.location.href;
    const urlObj = new URL(currentUrl);
    
    // Support both NFC ID and Session Hash formats: /[20-character-string]
    const pathParts = urlObj.pathname.split('/').filter(Boolean);
    if (pathParts.length === 1 && isValid20CharId(pathParts[0])) {
      // Determine if it's an NFC ID or session hash
      if (isValidNFCId(pathParts[0])) {
        return {
          nfc: pathParts[0]
        };
      } else {
        return {
          sessionHash: pathParts[0]
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error parsing NFC/Hash URL:', error);
    return null;
  }
};

/**
 * Validate 20-character ID format (NFC ID or Session Hash)
 */
export const isValid20CharId = (id: string): boolean => {
  return /^[A-Za-z0-9]{20}$/.test(id);
};

/**
 * Validate NFC ID format (20-character alphanumeric string starting with specific pattern)
 * NFC IDs typically start with 'B' for our system
 */
export const isValidNFCId = (nfcId: string): boolean => {
  return /^[A-Za-z0-9]{20}$/.test(nfcId) && nfcId.startsWith('B');
};

/**
 * Validate Session Hash format (20-character alphanumeric string, not starting with 'B')
 */
export const isValidSessionHash = (hash: string): boolean => {
  return /^[A-Za-z0-9]{20}$/.test(hash) && !hash.startsWith('B');
};

/**
 * Legacy: Validate restaurant ID format
 */
export const isValidRestaurantId = (id: string): boolean => {
  return /^REST\d{3}$/.test(id);
};

/**
 * Legacy: Validate table number
 */
export const isValidTableNumber = (tableNumber: string): boolean => {
  const num = parseInt(tableNumber, 10);
  return !isNaN(num) && num > 0 && num <= 999;
};

// REMOVED: Legacy patron token generation functions
// All tokens are now generated by Firebase backend for consistency and security
// PWC only stores and sends backend-generated tokens
